/**
 * FERP Portal - @Mention Input (DRR-062)
 *
 * Textarea with @mention autocomplete for annotation body.
 * Triggers actor/team search when user types '@' and shows
 * a popover with matching results.
 *
 * FERP CONSTRAINTS:
 * - Mantine v8.x ONLY
 * - Data-driven: suggestions fetched via callback (dumb frontend)
 * - No direct API calls — parent provides search function
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Textarea,
  Popover,
  Stack,
  UnstyledButton,
  Group,
  Text,
  Badge,
  Loader,
  Center,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconUser, IconUsers } from '@tabler/icons-react';

// ============================================================================
// TYPES
// ============================================================================

export interface MentionSuggestion {
  /** Actor or team ID */
  id: string;
  /** Display name */
  name: string;
  /** 'actor' or 'team' */
  type: 'actor' | 'team';
}

interface MentionInputProps {
  /** Current body text */
  value: string;
  /** Text change handler */
  onChange: (value: string) => void;
  /** Callback to search for actors/teams by query string */
  onSearchMentions?: (query: string) => Promise<MentionSuggestion[]>;
  /** Placeholder text */
  placeholder?: string;
  /** Min rows */
  minRows?: number;
  /** Max rows */
  maxRows?: number;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Textarea with @mention autocomplete.
 *
 * When the user types '@', a debounced search is triggered against
 * the parent-provided `onSearchMentions` callback. Selecting a suggestion
 * inserts `@{id}` into the text at the cursor position.
 *
 * If `onSearchMentions` is not provided, falls back to a plain Textarea.
 */
export function MentionInput({
  value,
  onChange,
  onSearchMentions,
  placeholder = 'Add a comment...',
  minRows = 2,
  maxRows = 6,
}: MentionInputProps) {
  const [mentionQuery, setMentionQuery] = useState('');
  const [debouncedQuery] = useDebouncedValue(mentionQuery, 200);
  const [suggestions, setSuggestions] = useState<MentionSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [cursorPos, setCursorPos] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Search for mentions when debounced query changes
  useEffect(() => {
    if (!debouncedQuery || !onSearchMentions) {
      setSuggestions([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    onSearchMentions(debouncedQuery)
      .then((results: any) => {
        if (!cancelled) {
          setSuggestions(results);
          setPopoverOpen(results.length > 0);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSuggestions([]);
          setPopoverOpen(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, onSearchMentions]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.currentTarget.value;
      const pos = e.currentTarget.selectionStart ?? newValue.length;
      onChange(newValue);
      setCursorPos(pos);

      if (!onSearchMentions) return;

      // Detect @mention trigger: find the last '@' before cursor
      const textBeforeCursor = newValue.slice(0, pos);
      const lastAt = textBeforeCursor.lastIndexOf('@');

      if (lastAt >= 0) {
        const charBeforeAt = lastAt > 0 ? textBeforeCursor[lastAt - 1] : ' ';
        // '@' must be at start or preceded by whitespace
        if (lastAt === 0 || charBeforeAt === ' ' || charBeforeAt === '\n') {
          const query = textBeforeCursor.slice(lastAt + 1);
          // Only trigger if query has no spaces (single-word mention)
          if (!query.includes(' ') && query.length > 0) {
            setMentionQuery(query);
            setMentionStart(lastAt);
            return;
          }
        }
      }

      // No active mention
      setMentionQuery('');
      setMentionStart(-1);
      setPopoverOpen(false);
    },
    [onChange, onSearchMentions],
  );

  const handleSelectSuggestion = useCallback(
    (suggestion: MentionSuggestion) => {
      if (mentionStart < 0) return;

      // Replace @query with @{id}
      const before = value.slice(0, mentionStart);
      const after = value.slice(cursorPos);
      const mention = `@${suggestion.id} `;
      const newValue = before + mention + after;

      onChange(newValue);
      setPopoverOpen(false);
      setMentionQuery('');
      setMentionStart(-1);

      // Restore focus after React re-render
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          const newPos = before.length + mention.length;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newPos, newPos);
        }
      });
    },
    [value, mentionStart, cursorPos, onChange],
  );

  // If no search callback, render plain Textarea
  if (!onSearchMentions) {
    return (
      <Textarea
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        minRows={minRows}
        maxRows={maxRows}
        autosize
      />
    );
  }

  return (
    <Popover
      opened={popoverOpen}
      onClose={() => setPopoverOpen(false)}
      position="bottom-start"
      width={250}
      shadow="md"
      trapFocus={false}
    >
      <Popover.Target>
        <Textarea
          ref={textareaRef}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          minRows={minRows}
          maxRows={maxRows}
          autosize
        />
      </Popover.Target>
      <Popover.Dropdown>
        {loading ? (
          <Center py="xs">
            <Loader size="xs" />
          </Center>
        ) : (
          <Stack gap={2}>
            {suggestions.map((s) => (
              <UnstyledButton
                key={s.id}
                onClick={() => handleSelectSuggestion(s)}
                p="xs"
                style={{
                  borderRadius: 'var(--mantine-radius-sm)',
                }}
                styles={{
                  root: {
                    '&:hover': {
                      backgroundColor: 'var(--mantine-color-gray-1)',
                    },
                  },
                }}
              >
                <Group gap="xs">
                  {s.type === 'team' ? (
                    <IconUsers size={14} />
                  ) : (
                    <IconUser size={14} />
                  )}
                  <Text size="sm">{s.name}</Text>
                  <Badge size="xs" variant="light" color="gray">
                    {s.type}
                  </Badge>
                </Group>
              </UnstyledButton>
            ))}
          </Stack>
        )}
      </Popover.Dropdown>
    </Popover>
  );
}
